import React, { Children } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/AcquireFooter';
import { SEOHead } from '../components/SEO/SEOHead';
export function PrivacyPage() {
  return <>
      <SEOHead title="Privacy Policy - AcquireFlow" description="Learn how AcquireFlow collects, uses, and protects your personal information." canonical="https://acquireflow.com/privacy-policy" data-id="element-2729" />

      <div className="flex flex-col min-h-screen bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707]" data-id="element-2730">
        <Navbar data-id="element-2731" />

        <main className="flex-grow" data-id="element-2732">
          {/* Hero Section */}
          <section className="relative w-full bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707] py-20 px-6" data-id="element-2733">
            <div className="max-w-4xl mx-auto text-center" data-id="element-2734">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm mb-6" data-id="element-2735">
                <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-2736" />
                <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-2737">
                  Legal
                </p>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight tracking-tight mb-6" data-id="element-2738">
                Privacy Policy
              </h1>

              <p className="text-lg text-[#b8bcc8]" data-id="element-2739">
                Last updated: January 2025
              </p>
            </div>
          </section>

          {/* Content Section */}
          <section className="relative w-full bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707] py-20 px-6" data-id="element-2740">
            <div className="max-w-4xl mx-auto" data-id="element-2741">
              <div className="bg-gradient-to-br from-[#1a1a1a]/80 to-[#0f0f0f]/80 border border-white/10 rounded-2xl p-8 md:p-12 backdrop-blur-md" data-id="element-2742">
                <div className="prose prose-invert prose-lg max-w-none" data-id="element-2743">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2744">
                    1. Introduction
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2745">
                    AcquireFlow ("we," "our," or "us") is committed to
                    protecting your privacy. This Privacy Policy explains how we
                    collect, use, disclose, and safeguard your information when
                    you use our Service.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2746">
                    2. Information We Collect
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3 mt-6" data-id="element-2747">
                    Personal Information
                  </h3>
                  <p className="text-[#b8bcc8] mb-4" data-id="element-2748">
                    We collect information that you provide directly to us,
                    including:
                  </p>
                  <ul className="list-disc list-inside text-[#b8bcc8] mb-6 space-y-2" data-id="element-2749">
                    <li data-id="element-2750">Name, email address, and contact information</li>
                    <li data-id="element-2751">Account credentials and authentication information</li>
                    <li data-id="element-2752">Payment and billing information</li>
                    <li data-id="element-2753">
                      Real estate investment preferences and property data
                    </li>
                    <li data-id="element-2754">Communications with our support team</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-white mb-3 mt-6" data-id="element-2755">
                    Automatically Collected Information
                  </h3>
                  <p className="text-[#b8bcc8] mb-4" data-id="element-2756">
                    When you use our Service, we automatically collect:
                  </p>
                  <ul className="list-disc list-inside text-[#b8bcc8] mb-6 space-y-2" data-id="element-2757">
                    <li data-id="element-2758">
                      Device information (IP address, browser type, operating
                      system)
                    </li>
                    <li data-id="element-2759">
                      Usage data (pages visited, features used, time spent)
                    </li>
                    <li data-id="element-2760">Cookies and similar tracking technologies</li>
                    <li data-id="element-2761">Log data and analytics information</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2762">
                    3. How We Use Your Information
                  </h2>
                  <p className="text-[#b8bcc8] mb-4" data-id="element-2763">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc list-inside text-[#b8bcc8] mb-6 space-y-2" data-id="element-2764">
                    <li data-id="element-2765">Provide, maintain, and improve our Service</li>
                    <li data-id="element-2766">Process transactions and send related information</li>
                    <li data-id="element-2767">
                      Send administrative messages, updates, and security alerts
                    </li>
                    <li data-id="element-2768">Respond to your comments and questions</li>
                    <li data-id="element-2769">Analyze usage patterns and optimize user experience</li>
                    <li data-id="element-2770">
                      Detect, prevent, and address technical issues and fraud
                    </li>
                    <li data-id="element-2771">Comply with legal obligations</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2772">
                    4. Information Sharing and Disclosure
                  </h2>
                  <p className="text-[#b8bcc8] mb-4" data-id="element-2773">
                    We may share your information in the following
                    circumstances:
                  </p>
                  <ul className="list-disc list-inside text-[#b8bcc8] mb-6 space-y-2" data-id="element-2774">
                    <li data-id="element-2775">
                      <strong data-id="element-2776">Service Providers:</strong> With third-party
                      vendors who perform services on our behalf
                    </li>
                    <li data-id="element-2777">
                      <strong data-id="element-2778">Business Transfers:</strong> In connection with
                      mergers, acquisitions, or asset sales
                    </li>
                    <li data-id="element-2779">
                      <strong data-id="element-2780">Legal Requirements:</strong> When required by law
                      or to protect our rights
                    </li>
                    <li data-id="element-2781">
                      <strong data-id="element-2782">With Your Consent:</strong> When you explicitly
                      authorize us to share information
                    </li>
                  </ul>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2783">
                    We do not sell your personal information to third parties.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2784">
                    5. Data Security
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2785">
                    We implement appropriate technical and organizational
                    measures to protect your information against unauthorized
                    access, alteration, disclosure, or destruction. However, no
                    method of transmission over the Internet is 100% secure, and
                    we cannot guarantee absolute security.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2786">
                    6. Data Retention
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2787">
                    We retain your information for as long as necessary to
                    provide our Service and fulfill the purposes outlined in
                    this Privacy Policy, unless a longer retention period is
                    required by law.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2788">
                    7. Your Rights and Choices
                  </h2>
                  <p className="text-[#b8bcc8] mb-4" data-id="element-2789">You have the right to:</p>
                  <ul className="list-disc list-inside text-[#b8bcc8] mb-6 space-y-2" data-id="element-2790">
                    <li data-id="element-2791">
                      Access and receive a copy of your personal information
                    </li>
                    <li data-id="element-2792">Correct inaccurate or incomplete information</li>
                    <li data-id="element-2793">Request deletion of your information</li>
                    <li data-id="element-2794">Object to or restrict certain processing activities</li>
                    <li data-id="element-2795">Opt-out of marketing communications</li>
                    <li data-id="element-2796">Export your data in a portable format</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2797">
                    8. Cookies and Tracking Technologies
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2798">
                    We use cookies and similar tracking technologies to collect
                    and track information about your use of our Service. You can
                    control cookies through your browser settings, but disabling
                    cookies may limit your ability to use certain features.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2799">
                    9. Third-Party Links
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2800">
                    Our Service may contain links to third-party websites. We
                    are not responsible for the privacy practices of these
                    external sites. We encourage you to review their privacy
                    policies.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2801">
                    10. Children's Privacy
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2802">
                    Our Service is not intended for individuals under the age of
                    18. We do not knowingly collect personal information from
                    children. If you believe we have collected information from
                    a child, please contact us immediately.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2803">
                    11. Changes to This Privacy Policy
                  </h2>
                  <p className="text-[#b8bcc8] mb-6" data-id="element-2804">
                    We may update this Privacy Policy from time to time. We will
                    notify you of any material changes by posting the new
                    Privacy Policy on this page and updating the "Last updated"
                    date.
                  </p>

                  <h2 className="text-2xl font-bold text-white mb-4 mt-8" data-id="element-2805">
                    12. Contact Us
                  </h2>
                  <p className="text-[#b8bcc8] mb-2" data-id="element-2806">
                    If you have questions about this Privacy Policy or our
                    privacy practices, please contact us:
                  </p>
                  <ul className="list-none text-[#b8bcc8] space-y-2" data-id="element-2807">
                    <li data-id="element-2808">
                      <strong data-id="element-2809">ACQUIRE FLOW LLC</strong>
                    </li>
                    <li data-id="element-2810">254 Chapman Rd, Ste 208 #25827</li>
                    <li data-id="element-2811">Newark, Delaware 19702 US</li>
                    <li data-id="element-2812">
                      Email:{' '}
                      <a href="mailto:support@acquireflow.ai" className="text-[#3ab795] hover:text-[#2da07d]" data-id="element-2813">
                        support@acquireflow.ai
                      </a>
                    </li>
                    <li data-id="element-2814">
                      Phone:{' '}
                      <a href="tel:+18052090120" className="text-[#3ab795] hover:text-[#2da07d]" data-id="element-2815">
                        +1 805-209-0120
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2816">
                <a href="/terms-and-conditions" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-medium hover:bg-white/20 transition-all duration-300" data-id="element-2817">
                  Terms & Conditions
                </a>
                <a href="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#3ab795] rounded-xl text-white font-medium hover:bg-[#2da07d] transition-all duration-300" data-id="element-2818">
                  Contact Us
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer data-id="element-2819" />
      </div>
    </>;
}